// src/onboarding/stepStyles.ts
import i18n from '@/i18n';
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import '@lottiefiles/lottie-player';
import type { StepOptions } from 'shepherd.js';
import { nextTick } from 'vue';
import type { Composer } from 'vue-i18n';
const t = (key: string) => (i18n.global as unknown as Composer).t(key);

let overlayRef: HTMLElement | null = null;

const onFocusIn = (e: Event) => {
  const target = e.target as HTMLElement;
  if (overlayRef && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.matches('[role="combobox"]'))) {
    console.log("[Onboarding] Focus sur Input détecté, cache l'overlay pour le clavier.");
    overlayRef.style.display = 'none';
  }
};

const onFocusOut = () => {
  if (overlayRef) {
    console.log("[Onboarding] Perte de focus, ré-affiche l'overlay.");
    overlayRef.style.display = 'block';
  }
};

function applyDialogFixes(step: StepOptions): StepOptions {
  step.classes = `${step.classes || ''} shepherd-step-dialog-level`.trim(); // 2. "Perce un trou" dans l'overlay

  const originalBeforeShow = step.beforeShowPromise;
  step.beforeShowPromise = async () => {
    if (originalBeforeShow) {
      await originalBeforeShow();
    }

    await nextTick();

    overlayRef = document.querySelector('.shepherd-modal-overlay');
    if (overlayRef) {
      overlayRef.style.zIndex = '1099';
    }

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
  };

  const originalWhenHide = step.when?.hide;
  step.when = {
    ...step.when,
    hide: function () {
      if (originalWhenHide) {
        originalWhenHide.call(this);
      }
      if (overlayRef) {
        overlayRef.style.zIndex = '';
      }
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      overlayRef = null;
    },
  };

  step.buttons = [];

  return step;
}
// --- Vos Helpers ---

export type LottieConfig = {
  placement?: 'top' | 'bottom' | 'left' | 'right';
  customTargetSelector?: string;
  zIndex?: string;
};

export type OnboardingStepType = 'info' | 'action' | 'waiting' | 'success';

export function createStyledStep(baseStep: StepOptions, stepType: OnboardingStepType = 'info'): StepOptions {
  const step = { ...baseStep };
  let classes = step.classes || '';

  switch (stepType) {
    case 'action':
      classes += ' shepherd-action-required';
      break;
    case 'waiting':
      classes += ' shepherd-waiting';
      step.buttons = [];
      break;
    case 'success':
      classes += ' shepherd-success';
      break;
    default:
      break;
  }
  step.classes = classes.trim();

  // Détecte automatiquement les étapes de dialogue et applique les corrections
  if (
    step.attachTo?.element &&
    typeof step.attachTo.element === 'string' &&
    (step.attachTo.element.includes('[data-pc-name="dialog"]') || step.attachTo.element.includes('.onboarding-choose-folder-dialog'))
  ) {
    return applyDialogFixes(step);
  }

  return step;
}

export function createActionStep(baseStep: StepOptions, lottieOptions?: LottieConfig): StepOptions {
  // 1. On récupère d'abord l'étape stylisée (avec la classe .shepherd-action-required)
  const step = createStyledStep(baseStep, 'action');

  // 2. Construction du DOM "Propre"
  const container = document.createElement('div');

  // Le Badge
  const badge = document.createElement('div');
  badge.className = 'onboarding-action-badge'; // Votre classe CSS
  badge.textContent = t('onboarding.common.actionRequired'); // Traduction

  // Le Texte de l'étape
  const content = document.createElement('div');
  content.innerHTML = (baseStep.text as string) || '';

  // Assemblage
  container.appendChild(badge);
  container.appendChild(content);

  // 3. On remplace le texte par notre élément DOM complet
  step.text = container;

  // 4. Ajouter automatiquement l'animation Lottie avec options personnalisables
  const placement = lottieOptions?.placement || 'bottom';
  const customTarget = lottieOptions?.customTargetSelector;
  const zIndex = lottieOptions?.zIndex;

  return attachLottieToStep(step, placement, customTarget, zIndex);
}
export function createWaitingStep(baseStep: StepOptions): StepOptions {
  return createStyledStep(baseStep, 'waiting');
}

export function createSuccessStep(baseStep: StepOptions): StepOptions {
  return createStyledStep(baseStep, 'success');
}

export function createInfoStep(baseStep: StepOptions): StepOptions {
  return createStyledStep(baseStep, 'info');
}

export function createInputStep(baseStep: StepOptions, lottieOptions?: LottieConfig): StepOptions {
  // On part d'une étape Action standard
  const step = createActionStep(baseStep, lottieOptions);

  // Local type extension to allow extra non-standard Shepherd options we set internally
  type LocalStep = StepOptions & { focusAfterRender?: boolean; keyboardNavigation?: boolean };

  // --- CONFIGURATION CRITIQUE POUR MOBILE ---

  // 1. Désactive le scroll automatique de Shepherd (on le gère manuellement)
  step.scrollTo = false;

  // 2. CRUCIAL POUR IOS : Empêche Shepherd de redonner le focus à la bulle
  // ou à l'élément après le rendu, ce qui ferait clignoter le clavier.
  (step as LocalStep).focusAfterRender = false;

  // 3. Désactive la navigation clavier (flèches gauche/droite) pour ne pas changer d'étape en écrivant
  (step as LocalStep).keyboardNavigation = false;

  // --- LE "FOCUS HACK" (Lobotomie) ---
  // On remplace la méthode .focus() de l'élément Shepherd par une fonction vide.
  // Cela empêche Shepherd de "voler" le focus à l'input quand le clavier resize la fenêtre.
  const originalShow = step.when?.show;
  step.when = step.when || {};

  step.when.show = function () {
    if (originalShow) {
      originalShow.call(this);
    }

    // On récupère l'élément HTML de la bulle/étape
    const tourElement = this.getElement() as HTMLElement;
    if (tourElement) {
      tourElement.focus = () => {
        /* Ne rien faire */
      };
    }
  };

  // --- SCROLL MANUEL ---
  const originalBeforeShow = step.beforeShowPromise;
  step.beforeShowPromise = async () => {
    if (originalBeforeShow) await originalBeforeShow();
    await nextTick();

    if (step.attachTo && typeof step.attachTo === 'object' && 'element' in step.attachTo) {
      const targetSelector = step.attachTo.element;
      const target = typeof targetSelector === 'string' ? document.querySelector(targetSelector) : (targetSelector as HTMLElement);

      if (target) {
        // Utiliser 'nearest' est plus sûr sur mobile que 'center' car cela évite
        // de cacher l'élément sous le clavier ou sous le header
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  };

  return step;
}

// --- GESTION LOTTIE (Doigt animé) ---
const LOTTIE_URL_DOWN = 'https://fonts.gstatic.com/s/e/notoemoji/latest/1f447/lottie.json'; // Doigt vers le bas
const LOTTIE_URL_UP = 'https://fonts.gstatic.com/s/e/notoemoji/latest/261d_fe0f/lottie.json'; // Doigt vers le haut
const LOTTIE_URL_LEFT = 'https://fonts.gstatic.com/s/e/notoemoji/latest/1f449/lottie.json'; // Doigt vers la gauche
const LOTTIE_URL_RIGHT = 'https://fonts.gstatic.com/s/e/notoemoji/latest/1f448/lottie.json'; // Doigt vers la droite
let lottieContainer: HTMLElement | null = null;
let cleanupFloatingUI: (() => void) | null = null;

function createAndPositionLottie(targetElement: HTMLElement, placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom', zIndex: string = '100000') {
  // Nettoyage préventif
  if (lottieContainer) lottieContainer.remove();
  if (cleanupFloatingUI) cleanupFloatingUI();

  // Choisir la bonne URL et rotation selon la position
  const getLottieConfig = (placement: 'top' | 'bottom' | 'left' | 'right') => {
    switch (placement) {
      case 'top':
        return { url: LOTTIE_URL_DOWN, rotation: '0deg' }; // Doigt vers le bas, pas de rotation
      case 'bottom':
        return { url: LOTTIE_URL_UP, rotation: '0deg' }; // Doigt vers le haut, pas de rotation
      case 'left':
        return { url: LOTTIE_URL_LEFT, rotation: '0deg' }; // Doigt vers la gauche, pas de rotation
      case 'right':
        return { url: LOTTIE_URL_RIGHT, rotation: '0deg' }; // Doigt vers la droite, pas de rotation
      default:
        return { url: LOTTIE_URL_UP, rotation: '0deg' };
    }
  };

  const { url, rotation } = getLottieConfig(placement);

  // Création du conteneur
  lottieContainer = document.createElement('div');
  lottieContainer.className = 'shepherd-lottie-pointer';
  lottieContainer.innerHTML = `
    <lottie-player
      src="${url}"
      background="transparent"
      speed="1"
      style="width: 60px; height: 60px; transform: rotate(${rotation});"
      loop
      autoplay
    ></lottie-player>
  `;

  document.body.appendChild(lottieContainer);

  // Positionnement dynamique
  cleanupFloatingUI = autoUpdate(targetElement, lottieContainer, () => {
    if (!lottieContainer) return;
    computePosition(targetElement, lottieContainer, {
      placement: placement,
      middleware: [offset(10), flip(), shift({ padding: 5 })],
    }).then(({ x, y }) => {
      Object.assign(lottieContainer!.style, {
        left: `${x}px`,
        top: `${y}px`,
        position: 'absolute',
        zIndex: zIndex,
        pointerEvents: 'none',
      });
    });
  });
}

/**
 * Attache une animation Lottie (doigt) à l'étape
 */
export function attachLottieToStep(
  step: StepOptions,
  placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom',
  customTargetSelector?: string,
  zIndex?: string,
): StepOptions {
  const newStep = { ...step };

  const originalShow = newStep.when?.show;
  const originalHide = newStep.when?.hide;

  newStep.when = {
    ...newStep.when,
    show: function () {
      // 1. Appel de la fonction originale (Focus hack, etc.)
      if (originalShow) originalShow.call(this);

      let target: HTMLElement | null = null;
      target = this.getTarget() as HTMLElement;

      // 3. Affichage du doigt
      if (target) {
        createAndPositionLottie(target, placement, zIndex);
      }
    },
    hide: function () {
      if (originalHide) originalHide.call(this);
      removeLottie();

      // Nettoyage
      if (cleanupFloatingUI) {
        cleanupFloatingUI();
        cleanupFloatingUI = null;
      }
      if (lottieContainer) {
        lottieContainer.remove();
        lottieContainer = null;
      }
    },
  };

  return newStep;
}

export function removeLottie() {
  // 1. Arrêter Floating UI
  if (cleanupFloatingUI) {
    cleanupFloatingUI();
    cleanupFloatingUI = null;
  }
  // 2. Supprimer le DOM element
  if (lottieContainer) {
    lottieContainer.remove();
    lottieContainer = null;
  }
}
